import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Avatar,
  Chip,
  useTheme,
  Collapse,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  CircularProgress,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MessageIcon from "@mui/icons-material/Message";
import NoteIcon from "@mui/icons-material/Note";
import EventIcon from "@mui/icons-material/Event";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RefreshIcon from "@mui/icons-material/Refresh";
import api from "../../../utils/api";

const ActivityWidget = ({ activities: propActivities, autoFetch = true }) => {
  const theme = useTheme();
  const [activities, setActivities] = useState(propActivities || []);
  const [loading, setLoading] = useState(false);
  const [currentActivityIndex, setCurrentActivityIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [autoPlay, setAutoPlay] = useState(true);
  const autoPlayRef = useRef(null);

  // Fetch activities if none provided and autoFetch is enabled
  useEffect(() => {
    if (autoFetch && (!propActivities || propActivities.length === 0)) {
      fetchRecentActivities();
    }
  }, [autoFetch, propActivities]);

  // Update local activities when propActivities change
  useEffect(() => {
    console.log("ActivityWidget: propActivities changed:", propActivities);
    if (propActivities && propActivities.length > 0) {
      setActivities(propActivities);
    }
  }, [propActivities]);

  const fetchRecentActivities = async () => {
    console.log("ActivityWidget: Fetching recent activities...");
    setLoading(true);
    try {
      const response = await api.activities.getRecent(20);
      console.log("ActivityWidget: Received activities:", response);
      setActivities(response);
    } catch (error) {
      console.error("Failed to fetch activities:", error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-advance activities every 5 seconds
  useEffect(() => {
    if (autoPlay && activities.length > 1) {
      autoPlayRef.current = setInterval(() => {
        setCurrentActivityIndex((prev) => (prev + 1) % activities.length);
      }, 5000);
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [autoPlay, activities.length]);

  // Pause auto-play when expanded
  useEffect(() => {
    if (expanded) {
      setAutoPlay(false);
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    } else {
      setAutoPlay(true);
    }
  }, [expanded]);

  const handleNext = () => {
    setCurrentActivityIndex((prev) => (prev + 1) % activities.length);
  };

  const handlePrevious = () => {
    setCurrentActivityIndex((prev) =>
      prev === 0 ? activities.length - 1 : prev - 1
    );
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  // Get icon based on activity type
  const getActivityIcon = (type) => {
    switch (type) {
      case "message":
        return <MessageIcon />;
      case "note":
      case "note_added":
        return <NoteIcon />;
      case "visit":
      case "visit_scheduled":
        return <EventIcon />;
      case "offer":
      case "offer_made":
        return <LocalOfferIcon />;
      case "status":
      case "status_changed":
        return <SwapHorizIcon />;
      default:
        return <NotificationsIcon />;
    }
  };

  // Get avatar color based on activity type
  const getAvatarColor = (type) => {
    switch (type) {
      case "message":
        return theme.palette.info.main;
      case "note":
      case "note_added":
        return theme.palette.warning.main;
      case "visit":
      case "visit_scheduled":
        return theme.palette.success.main;
      case "offer":
      case "offer_made":
        return theme.palette.error.main;
      case "status":
      case "status_changed":
        return theme.palette.secondary.main;
      default:
        return theme.palette.primary.main;
    }
  };

  // Format date to relative time
  const formatDate = (dateString) => {
    try {
      if (!dateString) return "Unknown date";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid date";

      const now = new Date();
      const diffMs = now - date;
      const diffSec = Math.round(diffMs / 1000);
      const diffMin = Math.round(diffSec / 60);
      const diffHour = Math.round(diffMin / 60);

      if (diffSec < 60) return "just now";
      if (diffMin < 60) return `${diffMin}m ago`;
      if (diffHour < 24) return `${diffHour}h ago`;
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  const getActivityContent = (activity) => {
    if (activity.type && activity.action && activity.actor && activity.target) {
      let formattedAction = activity.action.verb || "performed";
      if (formattedAction === "added") formattedAction = "added";
      else if (formattedAction === "edited") formattedAction = "edited";
      else if (formattedAction === "imported") formattedAction = "imported";
      else if (formattedAction === "noted") formattedAction = "added a note to";

      return {
        user: activity.actor.name || "Unknown User",
        action: formattedAction,
        target: activity.target.entityName || activity.target.entityType || "",
        details: activity.action.description || "",
        date: activity.timestamp,
        type: activity.type,
      };
    }

    if (activity.user && activity.action && activity.target) {
      return {
        user: activity.user,
        action: activity.action,
        target: activity.target,
        details: activity.details || "",
        date: activity.date,
        type: activity.type || "default",
      };
    }

    if (activity.text) {
      const authorName =
        activity.author && typeof activity.author === "object"
          ? activity.author.name || "User"
          : typeof activity.author === "string"
          ? activity.author
          : "User";

      return {
        user: authorName,
        action: "added a note",
        target: activity.category || "",
        details: activity.text || "",
        date: activity.date,
        type: "note",
      };
    }

    return {
      user: "User",
      action: "performed an action",
      target: "",
      details: "Activity details unavailable",
      date: new Date().toISOString(),
      type: "default",
    };
  };

  if (loading) {
    return (
      <Paper
        sx={{
          p: 1.5,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderTop: `4px solid ${theme.palette.secondary.main}`,
          borderRadius: 2,
          transition: "transform 0.2s, box-shadow 0.2s",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: 2,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: `${theme.palette.secondary.main}20`,
              color: theme.palette.secondary.main,
              mr: 1,
            }}
          >
            <NotificationsIcon fontSize="small" />
          </Box>
          <Typography variant="subtitle2" color="text.secondary">
            Recent Activity
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      </Paper>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <Paper
        sx={{
          p: 1.5,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderTop: `4px solid ${theme.palette.secondary.main}`,
          borderRadius: 2,
          transition: "transform 0.2s, box-shadow 0.2s",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: 2,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: `${theme.palette.secondary.main}20`,
              color: theme.palette.secondary.main,
              mr: 1,
            }}
          >
            <NotificationsIcon fontSize="small" />
          </Box>
          <Typography variant="subtitle2" color="text.secondary">
            Recent Activity
          </Typography>
        </Box>
        <Typography
          variant="body2"
          color="text.disabled"
          sx={{ textAlign: "center", py: 2 }}
        >
          No recent activities
        </Typography>
      </Paper>
    );
  }

  const currentActivity = activities[currentActivityIndex];
  const safeActivity = getActivityContent(currentActivity);
  const activityType = safeActivity.type || "default";

  return (
    <Paper
      sx={{
        p: 1.5,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderTop: `4px solid ${getAvatarColor(activityType)}`,
        borderRadius: 2,
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: 2,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: `${getAvatarColor(activityType)}20`,
              color: getAvatarColor(activityType),
              mr: 1,
            }}
          >
            {getActivityIcon(activityType)}
          </Box>
          <Typography variant="subtitle2" color="text.secondary">
            Recent Activity
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={fetchRecentActivities}
            sx={{ p: 0.5 }}
            disabled={loading}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={toggleExpanded} sx={{ p: 0.5 }}>
            {expanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      </Box>

      {/* Compact Activity View */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Avatar
            sx={{
              width: 24,
              height: 24,
              fontSize: "0.75rem",
              bgcolor: getAvatarColor(activityType),
              mr: 1,
            }}
          >
            {safeActivity.user.charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="body2" fontWeight="medium" sx={{ flex: 1 }}>
            {safeActivity.user}
          </Typography>
          <Chip
            label={formatDate(safeActivity.date)}
            size="small"
            variant="outlined"
            sx={{ fontSize: "0.625rem", height: 20 }}
          />
        </Box>

        <Typography
          variant="body2"
          color="text.primary"
          sx={{
            mb: 0.5,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            lineHeight: 1.3,
          }}
        >
          <Typography component="span" variant="body2" color="text.secondary">
            {safeActivity.action}{" "}
          </Typography>
          {safeActivity.target && (
            <Typography
              component="span"
              variant="body2"
              color="primary"
              fontWeight="medium"
            >
              {safeActivity.target}
            </Typography>
          )}
        </Typography>

        {safeActivity.details && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              fontStyle: "italic",
            }}
          >
            {safeActivity.details}
          </Typography>
        )}

        {/* Navigation Controls */}
        {activities.length > 1 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: "auto",
              pt: 1,
            }}
          >
            <IconButton size="small" onClick={handlePrevious} sx={{ p: 0.5 }}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>

            <Box sx={{ display: "flex", gap: 0.5 }}>
              {activities.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor:
                      index === currentActivityIndex
                        ? getAvatarColor(activityType)
                        : theme.palette.divider,
                    transition: "background-color 0.2s",
                  }}
                />
              ))}
            </Box>

            <IconButton size="small" onClick={handleNext} sx={{ p: 0.5 }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Expanded List View */}
      <Collapse in={expanded}>
        <Divider sx={{ my: 1 }} />
        <List sx={{ py: 0, maxHeight: 200, overflow: "auto" }}>
          {activities.slice(0, 5).map((activity, index) => {
            if (!activity || typeof activity !== "object") return null;

            const safeActivityItem = getActivityContent(activity);
            const activityTypeItem = safeActivityItem.type || "default";

            return (
              <React.Fragment key={activity.id || index}>
                <ListItem sx={{ py: 0.5, px: 0 }}>
                  <ListItemAvatar sx={{ minWidth: 32 }}>
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        fontSize: "0.75rem",
                        bgcolor: getAvatarColor(activityTypeItem),
                      }}
                    >
                      {safeActivityItem.user.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="caption" fontWeight="medium">
                        {safeActivityItem.user}{" "}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                        >
                          {safeActivityItem.action}
                        </Typography>
                        {safeActivityItem.target && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="primary"
                            fontWeight="medium"
                          >
                            {" "}
                            {safeActivityItem.target}
                          </Typography>
                        )}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.disabled">
                        {formatDate(safeActivityItem.date)}
                      </Typography>
                    }
                  />
                </ListItem>
                {index < Math.min(activities.length, 5) - 1 && (
                  <Divider variant="inset" component="li" />
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Collapse>
    </Paper>
  );
};

export default ActivityWidget;
